<template>
  <v-layout column v-if="value" class="nx-navigation-editor">
    <v-list>
      <draggable-tree ref="tree" @change="emitChanges" :data="items" draggable cross-tree>
        <div v-if="!data.isDragPlaceHolder" slot-scope="{data, store, vm}">
          <v-list-tile ripple>
            <v-list-tile-action>
              <v-icon
                v-if="data.children && data.children.length"
                @click="store.toggleOpen(data)"
                color="secondary">
                {{ data.open ? 'arrow_drop_down' : 'arrow_right' }}
              </v-icon>
              <v-icon v-else color="secondary">drag_indicator</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>{{ data.name }}</v-list-tile-title>
              <v-list-tile-sub-title>
                <v-icon small v-text="getNodeDescription(data).icon" class="mr-2"></v-icon>
                <span v-text="getNodeDescription(data).text"></span>
                <v-tooltip v-if="data.link.target" top>
                  <v-icon class="ml-1" small slot="activator">open_in_new</v-icon>
                  <span>Se abrirá en una nueva pestaña</span>
                </v-tooltip>
                <v-tooltip v-if="data.link.follow" top>
                  <v-icon class="ml-1" small slot="activator">power_off</v-icon>
                  <span><code>nofollow</code> está activado</span>
                </v-tooltip>
              </v-list-tile-sub-title>
            </v-list-tile-content>
            <v-list-tile-action>
              <v-layout row align-center>
                <v-btn class="mr-2" icon ripple @click="editNode(data)">
                  <v-icon color="primary">edit</v-icon>
                </v-btn>
                <v-btn class="mr-2" icon ripple @click="deleteNode(data, store)">
                  <v-icon color="primary">delete</v-icon>
                </v-btn>
                <v-tooltip top v-show="showAddNode(data, vm)">
                  <v-btn @click="addToNode(data)"
                         icon
                         ripple
                         slot="activator">
                    <v-icon color="primary">add_circle_outline</v-icon>
                  </v-btn>
                  <span>Añadir elemento anidado</span>
                </v-tooltip>
              </v-layout>
            </v-list-tile-action>
          </v-list-tile>
        </div>
      </draggable-tree>
    </v-list>
    <v-layout>
      <v-flex class="text-xs-center">
        <v-btn flat @click="add" color="secondary">
          <v-icon left>add</v-icon>
          {{ $t('message.common.add')}}
        </v-btn>
      </v-flex>
    </v-layout>
    <nx-navigation-wizard
      :active="wizardOpen"
      :value="wizardValue"
      @dismiss="wizardDismissed"
      @input="nodeInput"
      :formConfig="wizardFieldsConfig"
      :parentNode="wizardParentNode"
    >
    </nx-navigation-wizard>

    <nx-dialog-confirm
      :title="$t('message.navigation.selector.confirmTitle')"
      :text="$t('message.navigation.selector.confirmText')"
      ref="confirm"
    ></nx-dialog-confirm>

  </v-layout>
</template>

<script>
import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import config from '@/modules/navigation/config';
import Confirm from '@/components/dialogs/Confirm';
import { DraggableTree } from 'vue-draggable-nested-tree';
import Wizard from './wizard/NavigationWizard';

export default {
  name: 'nx-navigation-editor',
  components: {
    [Confirm.name]: Confirm,
    [Wizard.name]: Wizard,
    DraggableTree,
  },
  inject: {
    $getParentEditor: {
      default: null,
    },
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
    },
    maxDepth: {
      type: Number,
      default: 0, // Means no restriction.
    },
  },
  data() {
    return {
      items: [],
      wizardOpen: false,
      currentNode: null,
      parentNode: null,
      wizardParentNode: null,
    };
  },
  computed: {
    wizardValue() {
      if (!this.currentNode) {
        return {};
      }
      return this.$refs.tree.pure(this.currentNode);
    },
    wizardFieldsConfig() {
      let level = 0;
      if (this.parentNode) {
        level = this.parentNode._vm.level;
      } else if (this.currentNode) {
        level = this.currentNode._vm.level - 1;
      }
      return config.getWizardFieldsConfig(this.computedType, level);
    },
    computedMaxDepth() {
      if (this.maxDepth) {
        return this.maxDepth;
      }
      return get(this.$getParentEditor(), 'draft.maxDepth', null);
    },
    computedType() {
      if (this.type) {
        return this.type;
      }
      return get(this.$getParentEditor(), 'draft.itemsType', 'default');
    },
  },
  methods: {
    showAddNode(data, vm) {
      // Check for custom allowChildren configuration for current link type
      if (isUndefined(data.link.type)
        || isUndefined(config.navigationUrlConfig[data.link.type])
        || isUndefined(config.navigationUrlConfig[data.link.type].allowChildren)
      ) {
        return vm.level < this.computedMaxDepth;
      }

      if (typeof config.navigationUrlConfig[data.link.type].allowChildren === 'function') {
        return vm.level < this.computedMaxDepth
          && config.navigationUrlConfig[data.link.type].allowChildren(data, this.computedType);
      }

      return vm.level < this.computedMaxDepth && config.navigationUrlConfig[data.link.type].allowChildren;
    },
    emitChanges() {
      this.$emit('input', this.$refs.tree.getPureData());
    },
    add() {
      this.parentNode = null;
      this.currentNode = null;
      this.wizardOpen = true;
      this.wizardParentNode = { children: this.items, isRoot: true };
    },
    addToNode(node) {
      this.parentNode = node;
      this.currentNode = null;
      this.wizardOpen = true;
      this.wizardParentNode = node;
    },
    editNode(node) {
      this.parentNode = null;
      this.currentNode = node;
      this.wizardOpen = true;
      this.wizardParentNode = node.parent;
    },
    async deleteNode(node, tree) {
      const actualScrollPosition = document.documentElement.scrollTop;
      const confirm = await this.$refs.confirm.open();
      if (confirm) {
        tree.deleteNode(node);
        this.emitChanges(tree);
        this.$nextTick(() => {
          window.scrollTo(0, actualScrollPosition);
        });
      }
    },
    nodeInput(newNode) {
      const actualScrollPosition = document.documentElement.scrollTop;
      if (this.parentNode) {
        this.parentNode.children.push(newNode);
      } else if (this.currentNode) {
        this.currentNode = Object.assign(this.currentNode, newNode);
        if (!newNode.target) {
          delete this.currentNode.target;
        }
      } else {
        this.items.push(newNode);
      }
      this.wizardDismissed();
      this.emitChanges();
      this.$nextTick(() => {
        window.scrollTo(0, actualScrollPosition);
      });
    },
    wizardDismissed() {
      this.wizardOpen = false;
      this.currentNode = null;
      this.parentNode = null;
    },
    getNodeDescription(node) {
      const typeConfig = config.navigationUrlConfig[node.link.type];
      if (typeConfig) {
        return typeConfig.getNodeDescription(node);
      }
      return {
        icon: 'link_off',
        text: 'Entidad desconocida',
      };
    },
    initializeNodes(value) {
      // Set default values to the nodes, avoiding draggable-tree errors.
      return merge({}, {
        name: '',
        link: {
          url: '',
          type: 'empty',
          target: false,
          follow: false,
        },
      }, { link: value.url || {} }, value);
    },
  },
  mounted() {
    this.items = cloneDeep(this.value).map(this.initializeNodes);
  },
  watch: {
    value(val) {
      this.items = cloneDeep(val);
    },
  },
};
</script>

<style lang="stylus">
.nx-navigation-editor {
  .list-link {
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .he-tree {
  }

  .tree-node {
  }

  .tree-node-inner {
    cursor: pointer;
  }

  .draggable-placeholder {
  }

  .draggable-placeholder-inner {
    border: 1px dashed #4ED19C;
    box-sizing: border-box;
    background: rgba(0, 136, 249, 0.09);
    color: #4ED19C;
    text-align: center;
    padding: 0;
    display: flex;
    align-items: center;
  }
}
</style>
